import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { useNavigate } from 'react-router'
import {
  selectCountry,
  selectUserProfile,
} from '../../../common/category/store/slice'
import { Button } from '../../../common/html/button'
import { Checkbox } from '../../../common/html/checkbox'
import { Element } from '../../../common/html/element'
import { Radio } from '../../../common/html/radio'
import EventEmitter, {
  SHOW_POPUP_CONFIRMATION,
} from '../../../helpers/EventEmitter'
import { checkEmailValid, checkPhoneValid } from '../../../helpers/validation'
import { selectCompanyInfoData } from '../preview/store/slice'
import { getOrderRequest } from '../preview/store/thunk'
import { InputForm } from './InputForm'
import { SelectCustomForm } from './SelectCustomForm'
import {
  KEYS_FIELD_LIST,
  LIST_BUTTON_FOOTER,
  LIST_CHECKBOX,
  LIST_RADIO,
  TYPE_BUTTON_FOOTER,
} from './constants'
import { checkNameValid, checkRequiredDataValid } from './helper'
import classes from './index.module.scss'
import './lang/i18n'

const { NAME, EMAIL, PHONE, DATA_REQUIRED, REGIS_NUMBER, COUNTRY } =
  KEYS_FIELD_LIST
const DEFAULT_VALID = {
  email: true,
  phone: true,
  name: true,
  requiredData: true,
}

export const BusinessConciergeService = ({ setIsShow }) => {
  const dispatch = useDispatch()

  const [form, setForm] = useState({})
  const [isValid, setIsValid] = useState(DEFAULT_VALID)
  const [checkedItems, setCheckedItems] = useState([])
  const [orderType, setOrderType] = useState('Standard')

  const navigate = useNavigate()
  const company = useSelector(selectCompanyInfoData)
  const userProfile = useSelector(selectUserProfile)
  const country = useSelector(selectCountry)

  const hasCompany = Object.keys(company).length > 0

  const onSubmit = () => {
    const isEmailValid = checkEmailValid(form.email)
    const isPhoneValid = checkPhoneValid(form.phone)
    const isNameValid = checkNameValid(form.name)
    const isRequiredDataValid = checkRequiredDataValid(
      form.requiredData,
      checkedItems,
    )

    setIsValid({
      email: isEmailValid,
      name: isNameValid,
      phone: isPhoneValid,
      requiredData: isRequiredDataValid,
    })

    if (!isEmailValid || !isNameValid || !isRequiredDataValid || !isPhoneValid)
      return

    handleRequestConcierge()
  }

  const handleRequestConcierge = () => {
    const params = {
      Sku: '',
      CompanyName: hasCompany ? company.companyName : form[NAME],
      CountryCode: hasCompany ? company.countryCode : form[COUNTRY].value,
      CompanyId: company.companyId || 0,
      DataType: checkedItems,
      OrderType: orderType,
      Note: form[DATA_REQUIRED],
      Email: form.email,
      PhoneNumber: form.phone,
      Type: 'BussinessConcierge',
      RegisterNumber: form.registrationNumber,
    }

    dispatch(getOrderRequest(params))
      .unwrap()
      .then(() => {
        EventEmitter.dispatch(
          SHOW_POPUP_CONFIRMATION,
          'internationalPreview.CONFIRM_MESSAGE',
        )
        setIsShow(false)
      })
      .catch(() => {})
  }

  const onCancel = () => {
    setForm({})
    setIsValid(DEFAULT_VALID)
    navigate(-1)
  }

  const onChange = (key, event) => {
    const eventValue = key !== COUNTRY ? event.target.value : event

    if (key === EMAIL) {
      setIsValid({
        ...isValid,
        email: checkEmailValid(eventValue, true),
      })
    }

    if (key === PHONE) {
      setIsValid({
        ...isValid,
        phone: checkPhoneValid(eventValue),
      })
    }

    if (key === NAME) {
      setIsValid({
        ...isValid,
        name: checkNameValid(eventValue),
      })
    }

    if (key === DATA_REQUIRED) {
      setIsValid({
        ...isValid,
        requiredData: true,
      })
    }
    setForm({ ...form, [key]: eventValue })
  }

  const onChangeCheckbox = (value) => {
    if (checkedItems.includes(value)) {
      const newListCheckBox = checkedItems.filter((item) => item !== value)
      setCheckedItems(newListCheckBox)
    } else {
      const newListCheckBox = [...checkedItems, value]
      setCheckedItems(newListCheckBox)
      setIsValid({
        ...isValid,
        requiredData: true,
      })
    }
  }

  useEffect(() => {
    const defaultForm = {}
    if (hasCompany) {
      defaultForm.name = company.companyName
      defaultForm.registrationNumber = company.registrationNumber
    }
    if (userProfile) {
      defaultForm.email = userProfile?.email
      defaultForm.phone = userProfile?.companyPhone
    }
    if (Object.keys(country).length) {
      defaultForm.country = country
    }

    setForm(defaultForm)
  }, [userProfile, company])

  return (
    <Element className={classes.businessConciergeContainer}>
      <div className={'wh100 flexColumn'}>
        <div>
          <Translate
            value="businessConciergeService.BUSINESS_CONCIERGE_CONTENT"
            dangerousHTML
          />
        </div>
        <div style={{ marginTop: 12 }}>
          <InputForm
            title="businessConciergeService.COMPANY_NAME"
            containerStyle={{
              height: 28,
              marginBottom: !isValid.name ? 30 : 0,
            }}
            isValid={isValid.name}
            textInvalid={'businessConciergeService.REQUIRED_FIELD'}
            onChange={(e) => onChange(NAME, e)}
            readOnly={hasCompany}
            value={form.name}
          />
          <SelectCustomForm
            title="businessConciergeService.COUNTRY_JURISDICTION"
            containerStyle={{
              height: 28,
              marginTop: 24,
              position: 'relative',
            }}
            hasCompany={hasCompany}
            chooseItem={(item) => onChange(COUNTRY, item)}
            activeItem={form[COUNTRY]}
          />
          <InputForm
            title="businessConciergeService.REGISTRATION_NO"
            containerStyle={{ height: 28 }}
            value={form.registrationNumber}
            onChange={(e) => onChange(REGIS_NUMBER, e)}
            readOnly={hasCompany}
          />
          <div className={classes.checkBoxList}>
            {LIST_CHECKBOX.map((item) => (
              <Checkbox
                key={item.value}
                title={item.title}
                isI18n
                checked={checkedItems.includes(item.value)}
                isCheckOnClickText
                styleIcon={{ fontSize: 15 }}
                className={classes.checkBoxItem}
                onChange={() => onChangeCheckbox(item.value)}
              />
            ))}
          </div>
          <InputForm
            title="businessConciergeService.INFORMATION_REQUIREMENTS"
            containerStyle={{
              height: 100,
              marginBottom: !isValid.requiredData ? 6 : 0,
            }}
            isValid={isValid.requiredData}
            textInvalid={'businessConciergeService.REQUIRED_FIELD'}
            onChange={(e) => onChange(DATA_REQUIRED, e)}
            wrapClassName={classes.mt14}
          />
        </div>
        <div>
          <InputForm
            title="businessConciergeService.EMAIL_ADDRESS"
            containerStyle={{
              height: 28,
              marginBottom: !isValid.email ? 30 : 0,
            }}
            isValid={isValid.email}
            onChange={(e) => onChange(EMAIL, e)}
            textInvalid={
              typeof form.email === 'string' && !!form.email.trim()
                ? 'businessConciergeService.INVALID_EMAIL'
                : 'businessConciergeService.REQUIRED_FIELD'
            }
            value={form.email}
            readOnly={!!userProfile?.email}
          />
          <InputForm
            title="businessConciergeService.PHONE_NUMBER"
            containerStyle={{
              height: 28,
              marginBottom: !isValid.phone ? 30 : 0,
            }}
            value={form.phone}
            textInvalid={'businessConciergeService.INVALID_PHONE'}
            isValid={isValid.phone}
            onChange={(e) => onChange(PHONE, e)}
            readOnly={!!userProfile?.phone}
          />
          <div style={{ marginTop: 12 }}>
            <Translate
              value="businessConciergeService.RADIO_SELECT_NOTE"
              dangerousHTML
            />
          </div>
          <div className={classes.radioList}>
            {LIST_RADIO.map((item) => (
              <Radio
                key={item.title}
                title={item.title}
                isI18n
                value={item.value}
                appendStyle={{ marginBottom: 8 }}
                activeValue={orderType}
                onChange={setOrderType}
              />
            ))}
          </div>
        </div>
        <div
          className={['dFlex justifyContentEnd gap1', classes.footerList].join(
            ' ',
          )}
        >
          {LIST_BUTTON_FOOTER.map((item) => (
            <Button
              key={item.title}
              label={item.title}
              style={{
                border: '1px solid #D5D5D5',
                backgroundColor:
                  item.value === TYPE_BUTTON_FOOTER.CANCEL
                    ? '#FFFFFF'
                    : '#2c73e8',
                color:
                  item.value === TYPE_BUTTON_FOOTER.CANCEL
                    ? '#3A414B'
                    : '#ffffff',
              }}
              action={
                item.value === TYPE_BUTTON_FOOTER.CANCEL ? onCancel : onSubmit
              }
              isI18n
            />
          ))}
        </div>
      </div>
    </Element>
  )
}
